import type { ReportFilter } from '../models/filter.types'
import type {
  ReportLayoutConfig,
  ReportSectionTabConfig,
  SectionBuild,
} from '../models/layoutConfig.types'

// v3 `configs/Ecommerce/ProductPerformance.js` (268 ln). Port notes:
// - The two Meta rail entries collide on `name`, so per-tab request data keys off the tab's
//   first schedule tab (unique per entry); build ids stay verbatim — the v3 id collision
//   between the two Meta tabs is benign because the plain Meta tab hides via
//   `visibleRule.notValid` whenever the CPAS partition exists.
// - v3 `chartLineColumn` was dead (never a declared prop) and drops; `chartSortMetric` folds
//   into `sortField` (GA4 product keeps its explicit `sortField: 'mp_product_view'`);
//   `visibleType:'double'` + `showTableSlot` becomes shared chart-table composition.
// - v3 category `hideCols` (['campagin_name', 'product_image_url']) and the GA4 category
//   `removeColumns` (['product_name', 'product_id', 'product_brand']) wait for ORP-18643
//   column shaping.
// - `configProduct`'s return-level `entity_type: 'product'` folds into tab-level `entityType`
//   (v4's entity seam) — it is what kept the Category page's product group select alive in v3.
// - The Bundle Sales build's return-level `channel: 'ga4'` was a logo hint and drops.

const PRODUCT_PERFORMANCE_REPORT_ID = 17

interface ProductTabData {
  chartMetrics: string[]
  /** v3 `chartSortMetric` folded, except GA4 product which set a real `sortField`. */
  sortField: string
  chartDimensions?: string[]
  partitionOnly?: boolean
  filters?: ReportFilter[]
}

const PRODUCT_TAB_DATA: Record<string, ProductTabData> = {
  overview: { chartMetrics: ['cost', 'mp_product_revenue'], sortField: 'cost' },
  google_ads_product: {
    chartMetrics: ['cost', 'conversions_value'],
    sortField: 'cost',
    partitionOnly: true,
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'google_ads' }],
  },
  meta_ads_product: {
    chartMetrics: ['cost', 'purchase_value'],
    sortField: 'cost',
    partitionOnly: true,
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'meta_ads' }],
  },
  // FLAG: v3 declared `chartDimensions: ['product_id']` on the CPAS tab, but `configProduct`'s
  // operator precedence (`tab.chartDimensions || type === 'category' ? A : B`) made the runtime
  // value ['product_category']; ported as the actual v3 behavior — review whether the declared
  // intent (product_id) should be restored instead.
  meta_cpas_product_investment: {
    chartMetrics: ['cost', 'purchase_value'],
    sortField: 'cost',
    partitionOnly: true,
    chartDimensions: ['product_category'],
  },
  metrics_mp_product: {
    chartMetrics: ['cost', 'mp_product_revenue'],
    sortField: 'mp_product_view',
  },
  tiktok_ads_product: {
    chartMetrics: ['cost', 'purchase_value'],
    sortField: 'cost',
    partitionOnly: true,
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'tiktok_ads' }],
  },
  criteo_product: {
    chartMetrics: ['cost', 'purchase_value'],
    sortField: 'cost',
    partitionOnly: true,
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'criteo' }],
  },
}

const CATEGORY_TAB_DATA: Record<string, ProductTabData> = {
  overview: { chartMetrics: ['cost', 'purchases'], sortField: 'cost' },
  google_ads_product: {
    chartMetrics: ['cost', 'conversions_value'],
    sortField: 'cost',
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'google_ads' }],
  },
  meta_ads_product: {
    chartMetrics: ['cost', 'purchase_value'],
    sortField: 'cost',
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'meta_ads' }],
  },
  // v3 `removeColumns` (product_name/product_id/product_brand) waits for ORP-18643.
  metrics_mp_product: { chartMetrics: ['cost', 'mp_product_revenue'], sortField: 'cost' },
  tiktok_ads_product: {
    chartMetrics: ['cost', 'purchase_value'],
    sortField: 'cost',
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'tiktok_ads' }],
  },
  criteo_product: {
    chartMetrics: ['cost', 'conversions_value'],
    sortField: 'cost',
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'criteo' }],
  },
  // v3 listed 'rtb_house_product' twice in this tab's `tabs`; deduped (no semantic effect).
  rtb_house_product: {
    chartMetrics: ['cost', 'conversions_value'],
    sortField: 'cost',
    filters: [{ field: 'marketing_channel', match_mode: 'equals', value: 'rtb_house' }],
  },
}

// v3 `configProduct(tab, type)`.
const configProductBuild =
  (type: 'product' | 'category'): SectionBuild =>
  ({ tab }) => {
    const dataByTab = type === 'category' ? CATEGORY_TAB_DATA : PRODUCT_TAB_DATA
    const data = dataByTab[tab.tabs?.[0] ?? ''] ?? dataByTab.overview!
    return {
      id: `17-product-${type}-investment${tab.name}`,
      reportId: PRODUCT_PERFORMANCE_REPORT_ID,
      scheduleTabs: tab.tabs ?? [],
      view: 'table',
      composition: 'chart-table',
      chartDimensions:
        type === 'category'
          ? ['product_category']
          : (data.chartDimensions ?? ['product_name', 'product_image_url']),
      chartMetrics: data.chartMetrics,
      sortField: data.sortField,
      ...(type === 'category' ? { dimensions: ['product_category', 'product_brand'] } : {}),
      ...(data.partitionOnly ? { partitionOnly: true } : {}),
      filters: data.filters ?? [],
    }
  }

// v3 inline config on the `17-product-overview` section (performance-label top tabs; the
// tab name doubles as the backend `product_performance_label` filter value).
const productOverviewBuild: SectionBuild = ({ tab }) => ({
  id: `17-product-investment${tab.name}`,
  reportId: PRODUCT_PERFORMANCE_REPORT_ID,
  scheduleTabs: ['overview'],
  dimensions: [
    'product_image_url',
    'product_name',
    'product_id',
    'product_category',
    'product_performance_label',
  ],
  sortField: 'mp_product_revenue',
  filters: [{ field: 'product_performance_label', match_mode: 'equals', value: tab.name }],
})

const bundleSalesBuild: SectionBuild = () => ({
  id: '17',
  reportId: PRODUCT_PERFORMANCE_REPORT_ID,
  scheduleTabs: ['apriori_analysis'],
})

// Both Bundle Sales sections carry the identical single tab in v3.
const BUNDLE_SALES_TABS: ReportSectionTabConfig[] = [
  { name: 'Bundle Sales', tabs: ['apriori_analysis'] },
]

const PRODUCT_DETAIL_TABS: ReportSectionTabConfig[] = [
  { name: 'Overall', tabs: ['overview'], entityType: 'product' },
  { name: 'Google', tabs: ['google_ads_product'], entityType: 'product' },
  {
    name: 'Meta',
    tabs: ['meta_ads_product'],
    entityType: 'product',
    // v3: hide the plain Meta tab whenever the CPAS partition exists.
    visibleRule: {
      notValid: true,
      reportId: PRODUCT_PERFORMANCE_REPORT_ID,
      tabs: ['meta_cpas_product_investment'],
    },
  },
  { name: 'Meta', tabs: ['meta_cpas_product_investment'], entityType: 'product' },
  { name: 'GA4', tabs: ['metrics_mp_product'], entityType: 'product' },
  { name: 'Tiktok', tabs: ['tiktok_ads_product'], entityType: 'product' },
  { name: 'Criteo', tabs: ['criteo_product'], entityType: 'product' },
]

// v3 `CATEGORY_DETAILS` carried no per-tab entity_type; the shared `configProduct` return set
// `entity_type: 'product'` for both pages — folded here to tab level.
const CATEGORY_DETAIL_TABS: ReportSectionTabConfig[] = [
  { name: 'Overall', tabs: ['overview'], entityType: 'product' },
  { name: 'Google', tabs: ['google_ads_product'], entityType: 'product' },
  { name: 'Meta', tabs: ['meta_ads_product'], entityType: 'product' },
  { name: 'GA4', tabs: ['metrics_mp_product'], entityType: 'product' },
  { name: 'Tiktok', tabs: ['tiktok_ads_product'], entityType: 'product' },
  { name: 'Criteo', tabs: ['criteo_product'], entityType: 'product' },
  { name: 'RTB', tabs: ['rtb_house_product'], entityType: 'product' },
]

export const PRODUCT_PERFORMANCE: ReportLayoutConfig = [
  {
    name: 'Product',
    labelKey: 'reports.productPerformance.tabs.product',
    compareDefault: false,
    defaultPreset: 'Last 7 Days',
    sections: [
      {
        id: '17-product',
        reportId: PRODUCT_PERFORMANCE_REPORT_ID,
        build: configProductBuild('product'),
        tabs: PRODUCT_DETAIL_TABS,
      },
      {
        id: '17-product-overview',
        reportId: PRODUCT_PERFORMANCE_REPORT_ID,
        topTabMode: true,
        build: productOverviewBuild,
        tabs: [
          {
            name: 'Star Performers',
            labelKey: 'reports.productPerformance.productOverview.starPerformers',
            descriptionKey: 'reports.productPerformance.productOverview.starPerformersDescription',
            checkDimension: { field: 'product_performance_label', value: ['Star Performers'] },
            tabs: ['overview'],
          },
          {
            name: 'Hidden Potentials',
            labelKey: 'reports.productPerformance.productOverview.hiddenPotentials',
            checkDimension: { field: 'product_performance_label', value: ['Hidden Potentials'] },
            tabs: ['overview'],
          },
          {
            name: 'Budget Burners',
            labelKey: 'reports.productPerformance.productOverview.budgetBurners',
            checkDimension: { field: 'product_performance_label', value: ['Budget Burners'] },
            tabs: ['overview'],
          },
          {
            name: 'Ghost Products',
            labelKey: 'reports.productPerformance.productOverview.ghostProducts',
            checkDimension: { field: 'product_performance_label', value: ['Ghost Products'] },
            tabs: ['overview'],
          },
          {
            name: 'Average Performer',
            labelKey: 'reports.productPerformance.productOverview.averagePerformer',
            checkDimension: { field: 'product_performance_label', value: ['Average Performer'] },
            tabs: ['overview'],
          },
          {
            name: 'Tracking Gap',
            labelKey: 'reports.productPerformance.productOverview.trackingGap',
            checkDimension: { field: 'product_performance_label', value: ['Tracking Gap'] },
            tabs: ['overview'],
          },
        ],
      },
    ],
  },
  {
    name: 'Category',
    labelKey: 'reports.productPerformance.tabs.category',
    compareDefault: false,
    defaultPreset: 'Last 7 Days',
    sections: [
      {
        id: '17-category',
        reportId: PRODUCT_PERFORMANCE_REPORT_ID,
        build: configProductBuild('category'),
        tabs: CATEGORY_DETAIL_TABS,
      },
    ],
  },
  {
    name: 'Bundle Sales',
    labelKey: 'reports.productPerformance.tabs.bundleSales',
    compareDefault: false,
    sections: [
      {
        // v3 section had no id (bespoke component section); coined for the v4 anchor.
        id: '17-bundle-sales-chart',
        kind: 'pending',
        pendingNote: 'v3 component → BasketAnalysisChart (follow-up slice)',
        titleKey: 'reports.productPerformance.bundleSales.title',
        reportId: PRODUCT_PERFORMANCE_REPORT_ID,
        tabs: BUNDLE_SALES_TABS,
      },
      {
        id: '17-bundle-sales',
        titleKey: 'reports.productPerformance.bundleSales.title',
        reportId: PRODUCT_PERFORMANCE_REPORT_ID,
        hideChannels: true,
        build: bundleSalesBuild,
        tabs: BUNDLE_SALES_TABS,
      },
    ],
  },
]
